import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { Key } from "lucide-react";
import { callApi } from "../api/api";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import ManagePageHeader from "../components/ManagePageHeader";

export default function AccessReview() {
  const { instance, accounts } = useMsal();

  const [groupBy, setGroupBy] = useState("ROLE");
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [data, setData] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!showUserDropdown) return;

    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserDropdown]);

  /* ================= DERIVED USER ================= */
  const selectedUser = useMemo(() => {
    return users.find((u) => u.user_id === selectedUserId) || null;
  }, [users, selectedUserId]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) {
      return users.slice(0, 20);
    }

    const search = userSearch.toLowerCase();

    return users
      .filter((u) => {
        const fullName = `${u.first_name || ""} ${u.last_name || ""}`
          .trim()
          .toLowerCase();
        const email = u.email_address?.toLowerCase() || "";
        const displayName =
          fullName || u.email_address?.split("@")[0]?.toLowerCase() || "";
        const displayLabel = `${displayName} — ${email}`;

        return (
          fullName.includes(search) ||
          email.includes(search) ||
          displayLabel.includes(search)
        );
      })
      .slice(0, 20);
  }, [users, userSearch]);

  /* ================= LOAD USERS ================= */
  useEffect(() => {
    if (!accounts.length) {
      return undefined;
    }

    let active = true;

    (async () => {
      const res = await callApi(instance, accounts, "GET", "/api/admin/users");
      const list = res?.data?.data || [];

      console.log("Users API:", list);

      if (!active) {
        return;
      }

      setUsers(list);
      setSelectedUserId((prev) => {
        if (prev && list.some((u) => u.user_id === prev)) return prev;
        return null;
      });
    })();

    return () => {
      active = false;
    };
  }, [instance, accounts]);

  /* ================= LOAD ACCESS ================= */
  useEffect(() => {
    let active = true;

    (async () => {
      if (!selectedUserId) {
        if (active) {
          setData([]);
        }
        return;
      }

      if (active) {
        setData([]);
      }

      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/admin/access-review?userId=${selectedUserId}`,
      );

      if (active) {
        setData(res?.data?.data || []);
      }
    })();

    return () => {
      active = false;
    };
  }, [instance, accounts, selectedUserId]);

  /* ================= GROUP ================= */
  const grouped = useMemo(() => {
    const map = {};

    data.forEach((r) => {
      const key =
        groupBy === "ROLE"
          ? r.role_name || "UNKNOWN"
          : r.resource_type_code || "UNKNOWN";

      if (!map[key]) map[key] = [];
      map[key].push(r);
    });

    return map;
  }, [data, groupBy]);

  /* ================= CSV EXPORT ================= */
  const escapeCSV = (val) => {
    if (val === null || val === undefined) return "";
    return `"${String(val).replace(/"/g, '""')}"`;
  };

  const exportCSV = () => {
    if (!data.length) return;

    const headers = [
      "Resource Code",
      "Resource Name",
      "Resource Type",
      "Access",
      "Path",
      "Source Role",
    ];

    const rows = data.map((r) => [
      escapeCSV(r.resource_code),
      escapeCSV(r.resource_name),
      escapeCSV(r.resource_type_code),
      escapeCSV(r.access_level_code),
      escapeCSV(r.resource_path),
      escapeCSV(r.role_name),
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `access-review-${selectedUserId || "user"}.csv`;
    a.click();
  };

  /* ================= BADGES ================= */
  const accessBadge = useCallback((level) => {
    const map = {
      READ: { bg: "#ede9fe", color: "#6d28d9" },
      WRITE: { bg: "#fef3c7", color: "#b45309" },
      VIEW: { bg: "#dbeafe", color: "#1d4ed8" },
    };

    return <span style={{ ...styles.badge, ...map[level] }}>{level}</span>;
  }, []);

  const typeBadge = useCallback(
    (type) => <span style={styles.typeBadge}>{type || "—"}</span>,
    [],
  );

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapAccessCard = useCallback(
    (r) => ({
      id: `access-${r.resource_code}-${r.role_name}`,
      icon: <Key size={16} />,
      tone: r.access_level_code === "WRITE" ? "success" : "info",
      title: r.resource_code || "—",
      subtitle: r.resource_name || "—",
      meta: [
        { label: "Type", value: r.resource_type_code || "—" },
        { label: "Access", value: r.access_level_code || "—" },
        { label: "Role", value: r.role_name || "—" },
        { label: "Path", value: r.resource_path || "—" },
      ],
      badges: [
        {
          value: r.access_level_code || "—",
          tone: r.access_level_code === "WRITE" ? "success" : "info",
        },
      ],
    }),
    [],
  );

  const mapAccessDrawer = useCallback(
    (r) => ({
      title: r.resource_code || "—",
      fields: [
        { label: "Resource Name", value: r.resource_name || "—" },
        { label: "Resource Type", value: r.resource_type_code || "—" },
        { label: "Access Level", value: r.access_level_code || "—" },
        { label: "Path", value: r.resource_path || "—" },
        { label: "Source Role", value: r.role_name || "—" },
      ],
    }),
    [],
  );

  const renderGroupTable = useCallback(
    (groupRows) => (
      <ScrollableTable style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>RESOURCE CODE</th>
              <th style={styles.th}>NAME</th>
              <th style={styles.th}>TYPE</th>
              <th style={styles.th}>ACCESS</th>
              <th style={styles.th}>PATH</th>
              <th style={styles.th}>ROLE</th>
            </tr>
          </thead>

          <tbody>
            {groupRows.map((r, i) => (
              <tr
                key={i}
                style={styles.row}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
              >
                <td style={styles.td}>
                  <span style={styles.resourceCode}>{r.resource_code}</span>
                </td>

                <td style={styles.td}>
                  <span style={styles.resourceName}>
                    {r.resource_name || "—"}
                  </span>
                </td>

                <td style={styles.td}>{typeBadge(r.resource_type_code)}</td>

                <td style={styles.td}>
                  {accessBadge(r.access_level_code)}
                </td>

                <td style={styles.td}>
                  <span style={styles.path}>{r.resource_path || "—"}</span>
                </td>

                <td style={styles.td}>
                  <span style={styles.roleText}>{r.role_name}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>
    ),
    [accessBadge, typeBadge],
  );

  /* ================= UI ================= */

  return (
    <div
      className="page-container"
      style={{ ...styles.container, ...(isNarrow ? styles.containerNarrow : null) }}
    >
      <ManagePageHeader pageTitle="Access Review" />
      <div style={{ ...styles.header, ...(isNarrow ? styles.headerNarrow : null) }}>
        <div style={isNarrow ? styles.headerTitleBlock : undefined}>
          <h2 style={styles.title}>Effective Access Review</h2>

          <p style={styles.sub}>
            Inspect all effective permissions and inherited access across
            assigned roles.
          </p>
        </div>

        <button
          style={{
            ...styles.exportBtn,
            ...(isNarrow ? styles.exportBtnNarrow : null),
            opacity: data.length ? 1 : 0.55,
            cursor: data.length ? "pointer" : "not-allowed",
          }}
          onMouseEnter={(e) => {
            if (!data.length) return;
            e.currentTarget.style.background = "#f8fafc";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#fff";
          }}
          onClick={exportCSV}
          disabled={!data.length}
        >
          Export CSV
        </button>
      </div>

      <div style={styles.filterCard}>
        <div style={{ ...styles.toolbar, ...(isNarrow ? styles.toolbarNarrow : null) }}>
        {/* USER SELECT */}
        <div
          style={{
            ...styles.filterGroup,
            ...(isNarrow ? styles.filterGroupNarrow : null),
          }}
        >
          <label style={styles.label}>User</label>
          <div ref={userDropdownRef} style={styles.searchableWrapper}>
            <input
              type="text"
              placeholder="Search user by name or email"
              style={styles.input}
              value={userSearch}
              onFocus={() => setShowUserDropdown(true)}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setShowUserDropdown(true);
                setSelectedUserId(null);
              }}
            />

            {showUserDropdown && (
              <div style={styles.userDropdown} className="scrollable">
                {filteredUsers.length === 0 ? (
                  <div style={styles.userOptionEmpty}>No users found</div>
                ) : (
                  filteredUsers.map((u) => {
                    const displayName =
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.email_address?.split("@")[0];

                    return (
                      <div
                        key={u.user_id}
                        style={styles.userOption}
                        onClick={() => {
                          setSelectedUserId(u.user_id);

                          setUserSearch(`${displayName} — ${u.email_address}`);

                          setShowUserDropdown(false);
                        }}
                      >
                        <div style={styles.userOptionName}>{displayName}</div>

                        <div style={styles.userOptionEmail}>
                          {u.email_address}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* GROUP */}
        <div
          style={{
            ...styles.filterGroup,
            ...(isNarrow ? styles.filterGroupNarrow : null),
          }}
        >
          <label style={styles.label}>Group by</label>
          <select
            style={styles.input}
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="ROLE">Source Role</option>
            <option value="TYPE">Resource Type</option>
          </select>
        </div>

        <span
          style={{ ...styles.count, ...(isNarrow ? styles.countNarrow : null) }}
        >
          {data.length} resources
        </span>
        </div>
      </div>
      {selectedUser && (
        <div style={styles.userCard}>
          <div>
            <div style={styles.userTitle}>
              {`${selectedUser.first_name || ""} ${selectedUser.last_name || ""}`.trim() ||
                selectedUser.email_address?.split("@")[0]}
            </div>

            <div style={styles.email}>{selectedUser.email_address}</div>

            <div style={styles.userMeta}>
              Effective permissions inherited through assigned RBAC roles
            </div>
          </div>
        </div>
      )}
      {/* DATA */}
      {Object.entries(grouped).map(([key, groupRows]) => (
        <div key={key} style={styles.groupCard}>
          <div style={styles.groupHeader}>
            {key.toUpperCase()} ({groupRows.length})
          </div>
          <ResponsiveTableCards
            breakpoint={1024}
            data={groupRows}
            loading={false}
            emptyTitle="No Resources"
            emptySub="No resources found in this group."
            cardMapper={mapAccessCard}
            drawerMapper={mapAccessDrawer}
            renderTable={() => renderGroupTable(groupRows)}
          />
        </div>
      ))}
      {!selectedUserId && (
        <div style={styles.empty}>No user selected.</div>
      )}
      {selectedUserId && data.length === 0 && (
        <div style={styles.empty}>
          No access mappings found for the selected user.
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  containerNarrow: {
    minWidth: 0,
  },

  searchableWrapper: {
    position: "relative",
    width: "100%",
  },

  userDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    marginTop: "6px",
    maxHeight: "320px",
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 1000,
    boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
  },

  userOption: {
    padding: "12px 14px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
  },

  userOptionName: {
    fontWeight: "600",
    color: "#0f172a",
    fontSize: "14px",
  },

  userOptionEmail: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px",
  },

  userOptionEmpty: {
    padding: "14px",
    color: "#64748b",
    fontSize: "13px",
  },

  /* ================= HEADER ================= */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "20px",
  },

  headerNarrow: {
    flexDirection: "column",
    alignItems: "stretch",
  },

  headerTitleBlock: {
    minWidth: 0,
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  exportBtn: {
    border: "1px solid #dbe2ea",
    padding: "12px 16px",
    borderRadius: "10px",
    background: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.2s ease",
    color: "#0f172a",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },

  exportBtnNarrow: {
    width: "100%",
  },

  /* ================= FILTERS ================= */

  filterCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "20px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },

  toolbar: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },

  toolbarNarrow: {
    flexDirection: "column",
    alignItems: "stretch",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: "1 1 0",
    minWidth: 0,
  },

  filterGroupNarrow: {
    width: "100%",
    minWidth: 0,
    maxWidth: "none",
    flex: "1 1 auto",
  },

  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    color: "#0f172a",
    boxSizing: "border-box",
  },

  count: {
    flexShrink: 0,
    marginLeft: "auto",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "500",
    whiteSpace: "nowrap",
    paddingBottom: "12px",
  },

  countNarrow: {
    marginLeft: 0,
    paddingBottom: 0,
    textAlign: "right",
  },

  /* ================= USER CARD ================= */

  userCard: {
    background: "#fff",
    padding: "20px",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  userTitle: {
    fontWeight: "700",
    fontSize: "16px",
    color: "#0f172a",
    marginBottom: "6px",
  },

  email: {
    color: "#64748b",
    fontWeight: "400",
    fontSize: "14px",
  },

  userMeta: {
    marginTop: "10px",
    fontSize: "13px",
    color: "#64748b",
  },

  /* ================= GROUP CARD ================= */

  groupCard: {
    background: "#fff",
    borderRadius: "18px",
    border: "1px solid #e2e8f0",
    marginBottom: "24px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  groupHeader: {
    padding: "18px 22px",
    fontWeight: "700",
    fontSize: "14px",
    color: "#0f172a",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    letterSpacing: "0.02em",
  },

  /* ================= TABLE ================= */

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "960px",
  },

  th: {
    textAlign: "left",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.06em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
  },

  row: {
    transition: "background 0.2s ease",
  },

  td: {
    padding: "18px",
    borderBottom: "1px solid #f8fafc",
    verticalAlign: "middle",
    fontSize: "14px",
    color: "#0f172a",
  },

  resourceCode: {
    fontWeight: "600",
    fontSize: "14px",
    color: "#0f172a",
  },

  resourceName: {
    fontWeight: "500",
    color: "#0f172a",
  },

  path: {
    fontSize: "14px",
    color: "#64748b",
  },

  roleText: {
    color: "#475569",
    fontWeight: "500",
  },

  /* ================= BADGES ================= */

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#e2e8f0",
    color: "#334155",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  empty: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    padding: "48px 24px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "14px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },
};
